import { useMemo } from 'react'
import { UsePaginationResult } from './types'

/**
 * Hook personalizado para calcular qué números de página mostrar
 * Implementa lógica inteligente para mostrar solo páginas relevantes
 */
export const usePagination = (
  currentPage: number,
  totalPages: number,
  maxVisible: number = 5
): UsePaginationResult => {
  return useMemo(() => {
    if (totalPages <= 1) {
      return {
        pages: totalPages === 1 ? [1] : [],
        showStartEllipsis: false,
        showEndEllipsis: false,
      }
    }

    if (totalPages <= maxVisible) {
      // Si hay pocas páginas, mostrar todas
      return {
        pages: Array.from({ length: totalPages }, (_, i) => i + 1),
        showStartEllipsis: false,
        showEndEllipsis: false,
      }
    }

    const pages: number[] = []
    let showStartEllipsis = false
    let showEndEllipsis = false

    // Calcular cuántas páginas mostrar a cada lado de la actual
    const sideCount = Math.floor((maxVisible - 1) / 2) // -1 por la página actual

    let startPage = Math.max(1, currentPage - sideCount)
    let endPage = Math.min(totalPages, currentPage + sideCount)

    // Ajustar si estamos cerca del inicio
    if (currentPage <= sideCount + 1) {
      endPage = Math.min(maxVisible, totalPages)
      startPage = 1
    }

    // Ajustar si estamos cerca del final
    if (currentPage >= totalPages - sideCount) {
      startPage = Math.max(1, totalPages - maxVisible + 1)
      endPage = totalPages
    }

    // Agregar primera página
    if (startPage > 1) {
      pages.push(1)
      if (startPage > 2) {
        showStartEllipsis = true
      }
    }

    // Agregar páginas intermedias
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    // Agregar última página
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        showEndEllipsis = true
      }
      pages.push(totalPages)
    }

    return {
      pages: Array.from(new Set(pages)).sort((a, b) => a - b), // Eliminar duplicados y ordenar
      showStartEllipsis,
      showEndEllipsis,
    }
  }, [currentPage, totalPages, maxVisible])
}

